import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { rccColors } from "./colors";
import { useRccTypography } from "./typography";
import { formatTeamsData } from "../components/ChartBuilder/teamFilterComponents";

const ALL_TEAMS_VALUE = "";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },
  label: {
    color: rccColors.neutral[700],
    whiteSpace: "nowrap",
  },
  formControl: {
    minWidth: 180,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  goButton: {
    minWidth: 48,
    textTransform: "none",
    fontWeight: 600,
  },
}));

/**
 * Global RCC team filter: "Team View" dropdown + Go (admin / second-level+ managers only).
 * @param {{ onApply: (teamName: string | null) => void, disabled?: boolean }} props
 */
function RccTeamFilterBar({ onApply, disabled = false }) {
  const classes = useStyles();
  const typography = useRccTypography();
  const {
    rccTeamFilterEligible,
    rccDescendantTeams,
    rccDescendantTeamHierarchy,
    rccDescendantTeamsLoaded,
    rccViewAsTeam,
    rccFeatureFlags,
  } = useSelector((state) => state.app || {});

  const teamsList = useMemo(() => {
    if (Array.isArray(rccDescendantTeamHierarchy) && rccDescendantTeamHierarchy.length > 0) {
      return formatTeamsData(rccDescendantTeamHierarchy);
    }
    // Fallback to flat list if no hierarchy available
    return (rccDescendantTeams || []).map((name) => ({
      label: name,
      value: name,
      parentValue: null,
      isParent: false,
      indent: 0,
    }));
  }, [rccDescendantTeamHierarchy, rccDescendantTeams]);

  const [pending, setPending] = useState(ALL_TEAMS_VALUE);

  useEffect(() => {
    setPending(rccViewAsTeam ? String(rccViewAsTeam) : ALL_TEAMS_VALUE);
  }, [rccViewAsTeam]);

  if (!rccDescendantTeamsLoaded || !rccTeamFilterEligible || !rccFeatureFlags?.Team_Filter) {
    return null;
  }

  const handleGo = () => {
    const team =
      pending === ALL_TEAMS_VALUE || !String(pending).trim()
        ? null
        : String(pending).trim();
    onApply(team);
  };

  return (
    <Box className={classes.root}>
      <Typography className={`${typography.textSmallMedium} ${classes.label}`}>
        Team:
      </Typography>
      <FormControl
        variant="outlined"
        size="small"
        className={classes.formControl}
        disabled={disabled}
      >
        <Select
          value={pending}
          onChange={(e) => setPending(e.target.value)}
          displayEmpty
          renderValue={(selected) => {
            if (selected === ALL_TEAMS_VALUE || selected === null || selected === undefined) {
              return "All";
            }
            return selected;
          }}
        >
          <MenuItem value={ALL_TEAMS_VALUE}>All</MenuItem>
          {teamsList
            .filter((option) => option.label !== "All")
            .map((option) => (
              <MenuItem
                key={option.label}
                value={option.label}
                style={{ marginLeft: `${option.indent * 1}rem` }}
              >
                {option.label}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <Button
        color="primary"
        variant="contained"
        size="small"
        className={classes.goButton}
        disabled={disabled}
        onClick={handleGo}
      >
        Go
      </Button>
    </Box>
  );
}

export default RccTeamFilterBar;
